import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Button,
  Dropdown,
  Tag,
  OverflowMenu,
  OverflowMenuItem,
  Pagination,
  Grid,
  Column,
  ClickableTile,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { NotificationContext } from "../layout/Layout";
import { AlertDialog, NotificationKinds } from "../common/CustomNotification";
import {
  InventoryItemAPI,
  InventoryLotAPI,
  InventoryLotStorageAPI,
} from "./InventoryService";
import LotEntryModal from "./LotEntryModal";
import RecordUsageModal from "./RecordUsageModal";
import LotAdjustmentModal from "./LotAdjustmentModal";
import DisposeLotModal from "./DisposeLotModal";
import UpdateQCStatusModal from "./UpdateQCStatusModal";
import LotDetailsPanel from "./LotDetailsPanel";
import LocationPickerModal from "../storage/LocationPicker/LocationPickerModal";
import {
  getDeepestLocationSelection,
  positionToCoordinate,
} from "../storage/LocationPicker/locationSelectionMapper";
import "./InventoryList.css";

const QC_TAG_KIND = {
  PASSED: "green",
  FAILED: "red",
  PENDING: "gray",
  QUARANTINED: "magenta",
};

const QC_GATE_STOCK_STATUS = {
  FAILED: { type: "qcFailed", id: "stock.status.qcFailed", kind: "red" },
  QUARANTINED: {
    type: "quarantined",
    id: "stock.status.quarantined",
    kind: "magenta",
  },
};
const PENDING_QC_STOCK_STATUS = {
  type: "pendingQc",
  id: "stock.status.pendingQc",
  kind: "cyan",
};

const daysToExpiry = (lot) =>
  Math.floor(
    (new Date(lot.expirationDate) - new Date()) / (1000 * 60 * 60 * 24),
  );

// A tile's count, the filter it applies to the table below and the stock-status
// tag on each row all read the rule named here, so an edit to a rule reaches
// the three of them together. lowStock counts items rather than lots, as noted
// where the counts are built.
const METRIC_RULES = {
  totalLots: () => true,
  lowStock: (lot, { lowStockItemIds }) =>
    lowStockItemIds.has(lot.inventoryItem?.id),
  expiringSoon: (lot, { items }) => {
    const item = items[lot.inventoryItem?.id];
    if (!item || !lot.expirationDate) return false;
    const days = daysToExpiry(lot);
    return days >= 0 && days <= (item.expirationAlertDays || 30);
  },
  expired: (lot, { items }) => {
    const item = items[lot.inventoryItem?.id];
    if (!item || !lot.expirationDate) return false;
    return daysToExpiry(lot) < 0;
  },
};

const METRIC_TILES = [
  { key: "totalLots", labelId: "inventory.metrics.totalLots" },
  {
    key: "lowStock",
    labelId: "inventory.metrics.lowStock",
    modifier: "metric-warning",
  },
  {
    key: "expiringSoon",
    labelId: "inventory.metrics.expiringSoon",
    modifier: "metric-expiring",
  },
  {
    key: "expired",
    labelId: "inventory.metrics.expired",
    modifier: "metric-expired",
  },
];

// `active` is the parent's tab state: Carbon keeps unselected TabPanels
// mounted, so without it a Catalog edit stays stale here until a reload.
const InventoryDashboard = ({ active = true }) => {
  const intl = useIntl();
  const { notificationVisible, setNotificationVisible, addNotification } =
    useContext(NotificationContext);

  const notify = useCallback(
    ({ kind = NotificationKinds.info, title, subtitle, message }) => {
      setNotificationVisible(true);
      addNotification({
        kind,
        title,
        subtitle,
        message,
      });
    },
    [addNotification, setNotificationVisible],
  );

  const [lots, setLots] = useState([]);
  const [items, setItems] = useState({});
  const [lowStockItemIds, setLowStockItemIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [activeMetric, setActiveMetric] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [lotModalOpen, setLotModalOpen] = useState(false);
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [disposalModalOpen, setDisposalModalOpen] = useState(false);
  const [qcStatusModalOpen, setQcStatusModalOpen] = useState(false);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [movingLot, setMovingLot] = useState(null);

  const [itemTypes, setItemTypes] = useState([
    { id: "ALL", text: intl.formatMessage({ id: "inventory.filter.all" }) },
  ]);

  const statusOptions = [
    { id: "ALL", text: intl.formatMessage({ id: "inventory.filter.all" }) },
    ...[
      "ACTIVE",
      "IN_USE",
      "EXPIRED",
      "CONSUMED",
      "QUARANTINED",
      "DISPOSED",
    ].map((status) => ({
      id: status,
      text: intl.formatMessage({ id: `lot.status.${status}` }),
    })),
  ];

  const headers = [
    {
      key: "name",
      header: intl.formatMessage({ id: "catalog.item.name" }),
    },
    {
      key: "lotNumber",
      header: intl.formatMessage({ id: "lot.number" }),
    },
    {
      key: "itemType",
      header: intl.formatMessage({ id: "catalog.item.type" }),
    },
    {
      key: "currentQuantity",
      header: intl.formatMessage({ id: "lot.currentQuantity" }),
    },
    {
      key: "location",
      header: intl.formatMessage({ id: "lot.storageLocation" }),
    },
    {
      key: "expirationDate",
      header: intl.formatMessage({ id: "lot.expirationDate" }),
    },
    {
      key: "status",
      header: intl.formatMessage({ id: "lot.status" }),
    },
    {
      key: "qcStatus",
      header: intl.formatMessage({ id: "lot.qcStatus" }),
    },
    {
      key: "stockStatus",
      header: intl.formatMessage({ id: "stock.status" }),
    },
    {
      key: "actions",
      header: intl.formatMessage({ id: "label.button.action" }),
    },
  ];

  useEffect(() => {
    const loadItemTypes = async () => {
      try {
        const types = await InventoryItemAPI.getItemTypes();
        setItemTypes([
          {
            id: "ALL",
            text: intl.formatMessage({ id: "inventory.filter.all" }),
          },
          ...types.map((type) => ({
            id: type,
            text: getItemTypeLabel(type),
          })),
        ]);
      } catch (err) {
        console.error("Error loading item types:", err);
      }
    };
    loadItemTypes();
  }, [intl]);

  // /items/types is server-driven, so a site-defined type that has no
  // inventory.itemType.* key falls back to the raw code.
  const getItemTypeLabel = (type) =>
    intl.formatMessage({
      id: `inventory.itemType.${type}`,
      defaultMessage: type,
    });

  useEffect(() => {
    if (active) fetchLots();
  }, [active]);

  const fetchLots = async () => {
    setLoading(true);
    try {
      // Low stock is the backend's call: it sums usable quantity across an
      // item's lots, which a per-lot check here cannot reproduce.
      const [lotsResponse, itemsResponse, lowStockResponse] = await Promise.all(
        [
          InventoryLotAPI.getAll(),
          InventoryItemAPI.getAll(),
          InventoryItemAPI.getLowStock(),
        ],
      );
      if (!isMountedRef.current) return;

      const validLots = Array.isArray(lotsResponse) ? [...lotsResponse] : [];
      // Newest lot first, the order the Storage Inventory Lots table uses: a
      // lot just added then lands on page one instead of on the last page.
      validLots.sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
      setLots(validLots);

      const itemsMap = Object.fromEntries(
        (Array.isArray(itemsResponse) ? itemsResponse : []).map((item) => [
          item.id,
          item,
        ]),
      );
      setItems(itemsMap);

      const lowStockIds = new Set(
        (Array.isArray(lowStockResponse) ? lowStockResponse : []).map(
          (item) => item.id,
        ),
      );
      setLowStockItemIds(lowStockIds);
    } catch (error) {
      console.error("Error fetching inventory:", error);
      if (!isMountedRef.current) return;
      setLots([]);
      setItems({});
      setLowStockItemIds(new Set());
      addNotification({
        kind: "error",
        title: intl.formatMessage({ id: "notification.error" }),
        message: "Error loading inventory data",
      });
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const getStockStatus = (lot) => {
    if (!lot || !lot.inventoryItem) return null;

    const item = items[lot.inventoryItem.id];
    if (!item) return null;

    const currentQty = lot.currentQuantity || 0;

    if (METRIC_RULES.expired(lot, { items })) {
      return {
        type: "expired",
        label: intl.formatMessage({ id: "stock.status.expired" }),
        kind: "red",
      };
    }

    if (METRIC_RULES.expiringSoon(lot, { items })) {
      return {
        type: "expiring",
        label: intl.formatMessage(
          { id: "stock.status.expiringIn" },
          { days: daysToExpiry(lot) },
        ),
        kind: "warm-gray",
      };
    }

    if (currentQty === 0) {
      return {
        type: "outOfStock",
        label: intl.formatMessage({ id: "stock.status.outOfStock" }),
        kind: "red",
      };
    }

    // Stock that exists but cannot be consumed: FEFO only picks QC-passed lots,
    // so name the gate instead of a reassuring "In Stock". Checked before low
    // stock: the gate is what blocks this lot, not the item total.
    const quarantinedByStatus = lot.status === "QUARANTINED";
    if (quarantinedByStatus || (lot.qcStatus && lot.qcStatus !== "PASSED")) {
      // Quarantine outranks the QC value, as the consume refusal does: a lot
      // received into quarantine keeps the default pending QC.
      const gate = quarantinedByStatus
        ? QC_GATE_STOCK_STATUS.QUARANTINED
        : QC_GATE_STOCK_STATUS[lot.qcStatus] || PENDING_QC_STOCK_STATUS;
      return {
        type: gate.type,
        label: intl.formatMessage({ id: gate.id }),
        kind: gate.kind,
      };
    }

    if (METRIC_RULES.lowStock(lot, { lowStockItemIds })) {
      return {
        type: "lowStock",
        label: intl.formatMessage({ id: "stock.status.lowStock" }),
        kind: "warm-gray",
      };
    }

    return {
      type: "inStock",
      label: intl.formatMessage({ id: "stock.status.inStock" }),
      kind: "green",
    };
  };

  const metricContext = { items, lowStockItemIds };

  const metrics = {
    totalLots: lots.length,
    // The backend reports low stock per item and the table lists those items'
    // lots, so this tile is the one whose number and filtered row count are
    // meant to differ.
    lowStock: lowStockItemIds.size,
    expiringSoon: lots.filter((lot) =>
      METRIC_RULES.expiringSoon(lot, metricContext),
    ).length,
    expired: lots.filter((lot) => METRIC_RULES.expired(lot, metricContext))
      .length,
  };

  const toggleMetricFilter = (key) => {
    setActiveMetric((current) => (current === key ? null : key));
    setPage(1);
  };

  const getFilteredLots = () => {
    let filtered = lots;

    if (activeMetric) {
      filtered = filtered.filter((lot) =>
        METRIC_RULES[activeMetric](lot, metricContext),
      );
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((lot) => {
        const item = items[lot.inventoryItem?.id];
        return (
          lot.lotNumber?.toLowerCase().includes(searchLower) ||
          lot.barcode?.toLowerCase().includes(searchLower) ||
          item?.name?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (typeFilter !== "ALL") {
      filtered = filtered.filter((lot) => {
        const item = items[lot.inventoryItem?.id];
        return item?.itemType === typeFilter;
      });
    }

    if (statusFilter !== "ALL") {
      filtered = filtered.filter((lot) => lot.status === statusFilter);
    }

    return filtered;
  };

  const filteredLots = getFilteredLots();

  const paginatedLots = filteredLots.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const rows = paginatedLots.map((lot) => {
    const item = items[lot.inventoryItem?.id];
    const stockStatus = getStockStatus(lot);

    return {
      id: String(lot.id),
      name: item?.name || "Unknown",
      lotNumber: lot.lotNumber,
      itemType: item?.itemType || "",
      currentQuantity: `${lot.currentQuantity || 0} ${item?.units || ""}`,
      location:
        lot.location?.hierarchicalPath ||
        intl.formatMessage({
          id: "storage.location.notAssigned",
          defaultMessage: "Not assigned",
        }),
      expirationDate: lot.expirationDate
        ? new Date(lot.expirationDate).toLocaleDateString()
        : "N/A",
      status: lot.status,
      qcStatus: lot.qcStatus || "PENDING",
      stockStatus: stockStatus,
    };
  });

  // Carbon reorders the rendered rows when a column is sorted, so the row
  // body has to resolve its lot by id rather than by position.
  const lotsById = new Map(paginatedLots.map((lot) => [String(lot.id), lot]));

  const handleLotSaved = () => {
    setLotModalOpen(false);
    setSelectedLot(null);
    fetchLots();
    notify({
      kind: NotificationKinds.success,
      title: intl.formatMessage({ id: "notification.success" }),
      message: intl.formatMessage({ id: "lot.save.success" }),
    });
  };

  const handleUsageSaved = () => {
    setUsageModalOpen(false);
    setSelectedLot(null);
    fetchLots();
    notify({
      kind: NotificationKinds.success,
      title: intl.formatMessage({ id: "notification.success" }),
      message: intl.formatMessage({ id: "usage.record.success" }),
    });
  };

  const handleAdjustmentSaved = () => {
    setAdjustmentModalOpen(false);
    setSelectedLot(null);
    fetchLots();
    notify({
      kind: NotificationKinds.success,
      title: intl.formatMessage({ id: "notification.success" }),
      message: intl.formatMessage({ id: "adjustment.success" }),
    });
  };

  const handleDisposalSaved = () => {
    setDisposalModalOpen(false);
    setSelectedLot(null);
    fetchLots();
    notify({
      kind: NotificationKinds.success,
      title: intl.formatMessage({ id: "notification.success" }),
      message: intl.formatMessage({ id: "disposal.success" }),
    });
  };

  const handleQCStatusSaved = () => {
    setQcStatusModalOpen(false);
    setSelectedLot(null);
    fetchLots();
    notify({
      kind: NotificationKinds.success,
      title: intl.formatMessage({ id: "notification.success" }),
      message: intl.formatMessage({ id: "qc.status.update.success" }),
    });
  };

  const handleEditLot = (lot) => {
    setSelectedLot(lot);
    setLotModalOpen(true);
  };

  const handleViewDetails = (lot) => {
    setSelectedLot(lot);
    setDetailsPanelOpen(true);
  };

  const handleMoveLocation = (lot) => {
    setMovingLot(lot);
    setLocationPickerOpen(true);
  };

  const handlePrintLabel = async (lot) => {
    try {
      const response = await InventoryLotAPI.printLabel(lot.id);
      const blob = new Blob([response.data], { type: response.contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notify({
        kind: NotificationKinds.error,
        title: intl.formatMessage({ id: "notification.error" }),
        message: intl.formatMessage({ id: "lot.label.print.failed" }),
      });
    }
  };

  const movingLotCurrentLocation = movingLot?.location?.hierarchicalPath
    ? {
        selection: {},
        hierarchicalPath: movingLot.location.hierarchicalPath,
        position: movingLot.location.positionCoordinate
          ? { mode: "text", value: movingLot.location.positionCoordinate }
          : null,
      }
    : null;

  const handleLocationConfirm = async ({
    selection,
    position,
    reason,
    notes,
  }) => {
    if (!movingLot) return;
    try {
      const deepest = getDeepestLocationSelection(selection, {
        requireAssignable: true,
      });
      const payload = {
        inventoryLotId: String(movingLot.id),
        locationId: deepest ? String(deepest.value.id) : null,
        locationType: deepest ? deepest.type : null,
        positionCoordinate: positionToCoordinate(position, {
          emptyValue: null,
        }),
        notes: notes || "",
      };
      if (movingLotCurrentLocation) {
        await InventoryLotStorageAPI.moveLocation({
          ...payload,
          reason: reason || "",
        });
      } else {
        await InventoryLotStorageAPI.assignLocation(payload);
      }
      setLocationPickerOpen(false);
      setMovingLot(null);
      fetchLots();
      notify({
        kind: NotificationKinds.success,
        title: intl.formatMessage({ id: "notification.success" }),
        message: intl.formatMessage({
          id: "storage.location.assigned.success",
          defaultMessage: "Location assigned successfully",
        }),
      });
    } catch (error) {
      console.error("Error updating lot location:", error);
      notify({
        kind: NotificationKinds.error,
        title: intl.formatMessage({ id: "notification.error" }),
        message:
          error.message ||
          intl.formatMessage({
            id: "storage.location.assigned.error",
            defaultMessage: "Failed to assign location",
          }),
      });
    }
  };

  return (
    <>
      {notificationVisible === true ? <AlertDialog /> : ""}
      <Grid className="inventory-metrics-grid" fullWidth={false}>
        {METRIC_TILES.map((tile) => (
          <Column
            key={tile.key}
            lg={4}
            md={2}
            sm={4}
            className="inventory-metric-column"
          >
            <ClickableTile
              className={[
                "inventory-metric-tile",
                tile.modifier,
                activeMetric === tile.key && "inventory-metric-tile--selected",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-pressed={activeMetric === tile.key}
              onClick={() => toggleMetricFilter(tile.key)}
            >
              <div className="metric-value">{metrics[tile.key]}</div>
              <div className="metric-label">
                <FormattedMessage id={tile.labelId} />
              </div>
            </ClickableTile>
          </Column>
        ))}
      </Grid>

      <DataTable rows={rows} headers={headers} isSortable>
        {({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getTableProps,
          getTableContainerProps,
        }) => (
          <TableContainer title="" description="" {...getTableContainerProps()}>
            <TableToolbar>
              <TableToolbarContent>
                <TableToolbarSearch
                  placeholder={intl.formatMessage({
                    id: "inventory.lot.search.placeholder",
                  })}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  value={searchTerm}
                />

                <Dropdown
                  id="inventory-dashboard-type-filter"
                  titleText=""
                  label={intl.formatMessage({
                    id: "inventory.filter.type",
                  })}
                  items={itemTypes}
                  itemToString={(item) => (item ? item.text : "")}
                  selectedItem={
                    itemTypes.find((t) => t.id === typeFilter) ?? null
                  }
                  onChange={({ selectedItem }) => {
                    setTypeFilter(selectedItem.id);
                    setPage(1);
                  }}
                  size="md"
                />

                <Dropdown
                  id="inventory-dashboard-status-filter"
                  titleText=""
                  label={intl.formatMessage({
                    id: "inventory.filter.status",
                  })}
                  items={statusOptions}
                  itemToString={(item) => (item ? item.text : "")}
                  selectedItem={
                    statusOptions.find((s) => s.id === statusFilter) ?? null
                  }
                  onChange={({ selectedItem }) => {
                    setStatusFilter(selectedItem.id);
                    setPage(1);
                  }}
                  size="md"
                />

                <Button
                  renderIcon={Add}
                  onClick={() => {
                    setSelectedLot(null);
                    setLotModalOpen(true);
                  }}
                >
                  <FormattedMessage id="inventory.add.button" />
                </Button>
              </TableToolbarContent>
            </TableToolbar>

            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader
                      key={header.key}
                      {...getHeaderProps({ header })}
                    >
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={headers.length}>Loading...</TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headers.length}>
                      No inventory items found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const lot = lotsById.get(row.id);
                    // DataTable syncs `rows` into its state in an effect, so
                    // for one render it can still list a just-filtered row.
                    if (!lot) return null;
                    return (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        {row.cells.map((cell) => {
                          if (cell.info.header === "stockStatus") {
                            const status = cell.value;
                            return (
                              <TableCell key={cell.id}>
                                {status && (
                                  <Tag type={status.kind}>{status.label}</Tag>
                                )}
                              </TableCell>
                            );
                          }

                          if (cell.info.header === "qcStatus") {
                            return (
                              <TableCell key={cell.id}>
                                <Tag type={QC_TAG_KIND[cell.value] || "gray"}>
                                  {intl.formatMessage({
                                    id: `lot.qcStatus.${cell.value}`,
                                    defaultMessage: cell.value,
                                  })}
                                </Tag>
                              </TableCell>
                            );
                          }

                          if (cell.info.header === "location") {
                            return (
                              <TableCell key={cell.id}>
                                <Tag
                                  type={
                                    lot.location?.hierarchicalPath
                                      ? "blue"
                                      : "gray"
                                  }
                                >
                                  {cell.value}
                                </Tag>
                              </TableCell>
                            );
                          }

                          if (cell.info.header === "actions") {
                            return (
                              <TableCell key={cell.id}>
                                <OverflowMenu
                                  size="sm"
                                  flipped
                                  ariaLabel={intl.formatMessage({
                                    id: "label.button.action",
                                  })}
                                >
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: "lot.details.view",
                                    })}
                                    onClick={() => handleViewDetails(lot)}
                                  />
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: "button.edit",
                                    })}
                                    onClick={() => handleEditLot(lot)}
                                  />
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: "usage.record.button",
                                    })}
                                    onClick={() => {
                                      setSelectedLot(lot);
                                      setUsageModalOpen(true);
                                    }}
                                  />
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: "adjustment.button",
                                    })}
                                    onClick={() => {
                                      setSelectedLot(lot);
                                      setAdjustmentModalOpen(true);
                                    }}
                                  />
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: "qc.status.update.button",
                                    })}
                                    onClick={() => {
                                      setSelectedLot(lot);
                                      setQcStatusModalOpen(true);
                                    }}
                                  />
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: lot.location?.hierarchicalPath
                                        ? "storage.location.move"
                                        : "storage.location.assign",
                                      defaultMessage: lot.location
                                        ?.hierarchicalPath
                                        ? "Move storage location"
                                        : "Assign storage location",
                                    })}
                                    onClick={() => handleMoveLocation(lot)}
                                  />
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: "lot.label.print",
                                    })}
                                    disabled={!lot.barcode}
                                    onClick={() => handlePrintLabel(lot)}
                                  />
                                  <OverflowMenuItem
                                    itemText={intl.formatMessage({
                                      id: "disposal.button",
                                    })}
                                    onClick={() => {
                                      setSelectedLot(lot);
                                      setDisposalModalOpen(true);
                                    }}
                                    isDelete
                                  />
                                </OverflowMenu>
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {!loading && rows.length > 0 && (
              <Pagination
                backwardText="Previous page"
                forwardText="Next page"
                itemsPerPageText="Items per page:"
                page={page}
                pageSize={pageSize}
                pageSizes={[10, 20, 30, 40, 50]}
                totalItems={filteredLots.length}
                onChange={({ page, pageSize }) => {
                  setPage(page);
                  setPageSize(pageSize);
                }}
              />
            )}
          </TableContainer>
        )}
      </DataTable>

      {/* Lot Entry Modal */}
      {lotModalOpen && (
        <LotEntryModal
          open={lotModalOpen}
          onClose={() => {
            setLotModalOpen(false);
            setSelectedLot(null);
          }}
          onSave={handleLotSaved}
          lot={selectedLot}
        />
      )}

      {/* Record Usage Modal */}
      {usageModalOpen && (
        <RecordUsageModal
          open={usageModalOpen}
          onClose={() => {
            setUsageModalOpen(false);
            setSelectedLot(null);
          }}
          onSave={handleUsageSaved}
          lot={selectedLot}
        />
      )}

      {/* Lot Adjustment Modal */}
      {adjustmentModalOpen && (
        <LotAdjustmentModal
          open={adjustmentModalOpen}
          onClose={() => {
            setAdjustmentModalOpen(false);
            setSelectedLot(null);
          }}
          onSave={handleAdjustmentSaved}
          lot={selectedLot}
        />
      )}

      {/* Dispose Lot Modal */}
      {disposalModalOpen && (
        <DisposeLotModal
          open={disposalModalOpen}
          onClose={() => {
            setDisposalModalOpen(false);
            setSelectedLot(null);
          }}
          onSave={handleDisposalSaved}
          lot={selectedLot}
        />
      )}

      {/* Update QC Status Modal */}
      {qcStatusModalOpen && (
        <UpdateQCStatusModal
          open={qcStatusModalOpen}
          onClose={() => {
            setQcStatusModalOpen(false);
            setSelectedLot(null);
          }}
          onSave={handleQCStatusSaved}
          lot={selectedLot}
        />
      )}

      {/* Lot Details Panel (Slide-out) */}
      <LotDetailsPanel
        open={detailsPanelOpen}
        onClose={() => {
          setDetailsPanelOpen(false);
          setSelectedLot(null);
        }}
        lot={selectedLot}
      />

      {/* Move/Assign Storage Location Modal */}
      <LocationPickerModal
        isOpen={locationPickerOpen}
        occupantType="INVENTORY_LOT"
        occupant={{
          label: movingLot?.lotNumber || "",
          type: items[movingLot?.inventoryItem?.id]?.name || "",
          status: movingLot?.status || "",
        }}
        currentLocation={movingLotCurrentLocation}
        onConfirm={handleLocationConfirm}
        onCancel={() => {
          setLocationPickerOpen(false);
          setMovingLot(null);
        }}
      />
    </>
  );
};

export default InventoryDashboard;
